import { inject, Injectable } from '@angular/core';
import {
  AnalyticsEvent,
  AnalyticsService,
  API_CONFIG,
  ApiCallingService,
  ApiCallingV2Service,
  ApiResponseModel,
  ResponseUtilityService
} from '@zayan-erp/shared';
import { Observable, map, tap } from 'rxjs';
import { AddendumProductPayload, CreateAddendumPayload } from '../models/deal-options.models';
import { SalesApsService } from './sales-aps.service';

@Injectable({
  providedIn: 'root'
})
export class AddendumService {
  readonly #api = inject(ApiCallingService);
  readonly #apiV2 = inject(ApiCallingV2Service);
  readonly #responseHandler = inject(ResponseUtilityService);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;
  #analytics = inject(AnalyticsService);
  #salesAps = inject(SalesApsService);

  createAddendum(payload: CreateAddendumPayload): Observable<void> {
    return this.#apiV2
      .postData<void>(
        `${this.baseUrl}/addendums`,
        { showLoader: true, buttonId: 'deal-add-addendum', showMessage: true },
        payload
      )
      .pipe(
        tap(() => {
          this.#analytics.capture(AnalyticsEvent.AddendumCreated, {
            lot_id: payload.lotId,
            ...(payload.dealId ? { deal_id: payload.dealId } : {})
          });
        })
      );
  }

  addProducts(addendumId: string, payload: AddendumProductPayload[]): Observable<void> {
    return this.#apiV2
      .postData<void>(
        `${this.baseUrl}/addendums/${addendumId}/products`,
        { showLoader: true, buttonId: 'deal-save-addendum-options', showMessage: false },
        payload
      )
      .pipe(
        tap(() => {
          const ctx = this.#salesAps.currentDealCtx();
          if (!ctx) return;
          this.#analytics.capture(AnalyticsEvent.AddendumOptionsAdded, {
            ...ctx,
            addendum_id: addendumId,
            option_count: payload.length
          });
        })
      );
  }

  createAddendumProductTax(payload: { addendumProductId: string; taxAuthorityId: string }): Observable<any> {
    return this.#api
      .postData('addendum-products', `${payload.addendumProductId}/taxes`, payload, true)
      .pipe(map((res: ApiResponseModel<any>) => this.#responseHandler.handleResponse<any>(res, res.message, true)));
  }
  deleteAddendumProductTax(addendumProductId: string, id: string): Observable<void> {
    return this.#api
      .deleteData('addendum-products', `${addendumProductId}/taxes/${id}`, true)
      .pipe(map((res: ApiResponseModel<void>) => this.#responseHandler.handleResponse(res, 'Tax deleted', true)));
  }

  getTaxRatesForAuthority(addendumProductId: string, taxAuthorityId: string): Observable<any> {
    return this.#api
      .getData(
        `addendum-products/${addendumProductId}`,
        `taxes/preview?taxAuthorityId=${taxAuthorityId}`,
        false,
        { showLoader: false, showMessage: false } as any,
        false
      )
      .pipe(map((res: ApiResponseModel<any>) => this.#responseHandler.handleResponse<any>(res, res.message, true)));
  }
}
